import React, { useState, useRef, useCallback } from 'react';
import { PhotoIcon, DocumentArrowUpIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';

interface ImageUploadProps {
  onImageSelect: (file: File) => void;
  currentImage?: string | null;
  validationErrors?: string[];
  disabled?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageSelect,
  currentImage,
  validationErrors = [],
  disabled = false
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(currentImage || null);
  const [uploadMethod, setUploadMethod] = useState<'browse' | 'drag' | 'paste' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAndSetImage = useCallback((file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      return false;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      return false;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    onImageSelect(file);
    return true;
  }, [onImageSelect]);

  const handleFileSelect = useCallback((file: File) => {
    if (validateAndSetImage(file)) {
      setUploadMethod('browse');
    }
  }, [validateAndSetImage]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      if (validateAndSetImage(imageFile)) {
        setUploadMethod('drag');
      }
    }
  }, [disabled, validateAndSetImage]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    if (disabled) return;

    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    
    if (imageItem) {
      const file = imageItem.getAsFile();
      if (file && validateAndSetImage(file)) {
        setUploadMethod('paste');
      }
    }
  }, [disabled, validateAndSetImage]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleBrowseClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  const clearImage = useCallback(() => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setUploadMethod(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [imagePreview]);

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center transition-colors
          ${isDragOver 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onPaste={handlePaste}
        onClick={handleBrowseClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        <div className="space-y-3">
          <div className="flex justify-center space-x-4">
            <PhotoIcon className="h-8 w-8 text-gray-400" />
            <DocumentArrowUpIcon className="h-8 w-8 text-gray-400" />
            <ClipboardDocumentIcon className="h-8 w-8 text-gray-400" />
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-900">
              Upload Equipment Image
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Click to browse, drag & drop, or paste from clipboard
            </p>
          </div>

          <div className="flex justify-center space-x-4 text-xs text-gray-500">
            <span className="flex items-center">
              <DocumentArrowUpIcon className="h-4 w-4 mr-1" />
              Browse
            </span>
            <span className="flex items-center">
              <PhotoIcon className="h-4 w-4 mr-1" />
              Drag & Drop
            </span>
            <span className="flex items-center">
              <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
              Paste (Ctrl+V)
            </span>
          </div>
        </div>

        {/* Upload Method Indicator */}
        {uploadMethod && (
          <div className="absolute top-2 right-2">
            <span className={`
              inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
              ${uploadMethod === 'browse' ? 'bg-blue-100 text-blue-800' :
                uploadMethod === 'drag' ? 'bg-green-100 text-green-800' :
                'bg-purple-100 text-purple-800'}
            `}>
              {uploadMethod === 'browse' ? '📁 Browsed' :
               uploadMethod === 'drag' ? '🎯 Dropped' :
               '📋 Pasted'}
            </span>
          </div>
        )}
      </div>

      {/* Image Preview */}
      {imagePreview && (
        <div className="relative">
          <div className="border rounded-lg overflow-hidden">
            <img
              src={imagePreview}
              alt="Equipment preview"
              className="w-full h-48 object-cover"
            />
          </div>
          <button
            type="button"
            onClick={clearImage}
            disabled={disabled}
            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Image Upload Issues
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <ul className="list-disc list-inside space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Requirements */}
      <div className="card">
      <div className="card-body">
        <p className="font-medium mb-1">Supported formats:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>JPG, PNG, GIF, WebP</li>
          <li>Maximum file size: 5MB</li>
          <li>Recommended: 800x600px or larger</li>
        </ul>
      </div>
    </div>
    </div>
  );
};

export default ImageUpload;
